import { SharedUser } from '../models/user';

type SortableUser = SharedUser;

/**
 * A viewer's own choice of the staged tile: `userId` stages that user,
 * null stages nobody. `epoch` is the latest presentation start known when
 * the choice was made; a share started after it takes over again.
 */
export interface StageOverride {
  userId: string | null;
  epoch: number;
}

/** The latest presentation start among `users`, 0 when nobody shares. */
export function presentationEpoch(users: SortableUser[]): number {
  return users.reduce((latest, user) => Math.max(latest, user.data.presentationStartedAt), 0);
}

/**
 * Resolves the user staged for this viewer. Every screen share presents,
 * so without a choice the latest share is staged and, when it ends, the
 * latest remaining one. The viewer's override wins until a newer share
 * starts; `superseded` then tells the caller to drop it.
 */
export function resolveStagedId(
  users: SortableUser[],
  override: StageOverride | null
): { stagedId: string | null; superseded: boolean } {
  const latest = presentationEpoch(users);
  const presenter =
    latest > 0 ? users.find((user) => user.data.presentationStartedAt === latest) : undefined;
  const presenterId = presenter?.id ?? null;
  if (!override) {
    return { stagedId: presenterId, superseded: false };
  }
  if (latest > override.epoch) {
    return { stagedId: presenterId, superseded: true };
  }
  if (override.userId === null) {
    return { stagedId: null, superseded: false };
  }
  // A staged user who left gives the stage back until they return.
  const present = users.some((user) => user.id === override.userId);
  return { stagedId: present ? override.userId : presenterId, superseded: false };
}

export function sortVideoList<T extends SortableUser>(
  users: T[],
  stagedId: string | null = null
): T[] {
  // helper to check whether a user has a screenshare enabled
  const hasScreenshare = (user: T) => !!user.screenCastStream;

  // helper to check whether a user has a webcam enabled
  const hasWebcam = (user: T) => !!user.camStream;

  return users.toSorted((a, b) => {
    // compare by stage (the presenter or the viewer's pick comes first)
    const stagedA = a.id === stagedId;
    const stagedB = b.id === stagedId;
    if (stagedA !== stagedB) return stagedA ? -1 : 1;

    // compare by talking status (sustained speaker)
    if (a.isActiveSpeaker !== b.isActiveSpeaker) return a.isActiveSpeaker ? -1 : 1;

    // compare by screen share status
    const streamingA = hasScreenshare(a);
    const streamingB = hasScreenshare(b);
    if (streamingA !== streamingB) return streamingA ? -1 : 1;

    // compare by raised hand
    const handA = !!a.data.hasRaisedHand;
    const handB = !!b.data.hasRaisedHand;
    if (handA !== handB) return handA ? -1 : 1;

    // compare by favorite status
    if (a.isFavorite !== b.isFavorite) return a.isFavorite ? -1 : 1;

    // compare by webcam status
    const camA = hasWebcam(a);
    const camB = hasWebcam(b);
    if (camA !== camB) return camA ? -1 : 1;

    // compare by online status
    if (a.data.status !== b.data.status) {
      return a.data.status === 'online' ? -1 : 1;
    }

    // compare by output mute status
    if (a.data.outputMuted !== b.data.outputMuted) {
      return a.data.outputMuted ? 1 : -1;
    }

    // compare by input mute status
    if (a.data.inputMuted !== b.data.inputMuted) {
      return a.data.inputMuted ? 1 : -1;
    }

    return 0;
  });
}

/**
 * Determines if a new user should replace an existing user in the visible video list.
 * Used to ensure stable rendering by only replacing users when there's a strictly
 * "better" candidate. Priority order matches sortVideoList:
 * stage > talking > screenshare > raised hand > webcam > online status.
 * isFavorite and mute state are intentionally NOT eviction dimensions: favorites stay
 * evictable and mute flips must not cause tile churn — both are sort-only tiebreakers.
 * @param newUser The candidate user to potentially add
 * @param oldUser The existing user in the visible slot
 * @param stagedId The user staged for this viewer (see resolveStagedId)
 * @returns true if newUser should replace oldUser
 */
export function isBetterUser<T extends SortableUser>(
  newUser: T,
  oldUser: T,
  stagedId: string | null = null
): boolean {
  // The staged user takes priority
  const newStaged = newUser.id === stagedId;
  const oldStaged = oldUser.id === stagedId;
  if (newStaged !== oldStaged) {
    return newStaged;
  }

  // Actively talking user beats silent user
  if (newUser.isActiveSpeaker !== oldUser.isActiveSpeaker) {
    return newUser.isActiveSpeaker;
  }

  // User with screenshare beats user without
  const newScreenshare = !!newUser.screenCastStream;
  const oldScreenshare = !!oldUser.screenCastStream;
  if (newScreenshare !== oldScreenshare) {
    return newScreenshare;
  }

  // User with raised hand beats user without
  const newRaisedHand = !!newUser.data.hasRaisedHand;
  const oldRaisedHand = !!oldUser.data.hasRaisedHand;
  if (newRaisedHand !== oldRaisedHand) {
    return newRaisedHand;
  }

  // User with camera beats user without
  const newCam = !!newUser.camStream;
  const oldCam = !!oldUser.camStream;
  if (newCam !== oldCam) {
    return newCam;
  }

  // Online user beats away user
  const newOnline = newUser.data.status === 'online';
  const oldOnline = oldUser.data.status === 'online';
  if (newOnline !== oldOnline) {
    return newOnline;
  }

  return false;
}

/**
 * Stabilizes the visible video list by preserving user positions and only
 * replacing users when a "better" candidate is available.
 * This prevents unnecessary tile movements when user properties change.
 *
 * @param previousVisible The previous list of visible users (preserves positions)
 * @param candidates The new sorted list of candidate users
 * @param maxSlots Maximum number of visible slots
 * @param stagedId The user staged for this viewer (see resolveStagedId)
 * @returns Stabilized list of users to display
 */
export function stabilizeVisibleList<T extends SortableUser>(
  previousVisible: T[],
  candidates: T[],
  maxSlots: number,
  stagedId: string | null = null
): T[] {
  // Handle edge case of no slots
  if (maxSlots <= 0) {
    return [];
  }

  // Step 1: Create result array preserving positions
  const result: (T | null)[] = new Array(maxSlots).fill(null);
  const usedCandidateIds = new Set<string>();

  // Step 1.5: Force the staged user into position 0
  const staged = candidates.find((c) => c.id === stagedId);
  if (staged) {
    result[0] = staged;
    usedCandidateIds.add(staged.id);
  }

  // Step 2: Keep existing users at their positions if still in candidates
  previousVisible.forEach((oldUser, index) => {
    if (index >= maxSlots) return;
    if (result[index] !== null) return; // slot already taken (e.g. by the staged user)

    const updatedUser = candidates.find((c) => c.id === oldUser.id);
    if (updatedUser && !usedCandidateIds.has(updatedUser.id)) {
      result[index] = updatedUser;
      usedCandidateIds.add(updatedUser.id);
    }
  });

  // Step 3: Get remaining candidates (not yet placed), sorted by priority
  const remainingCandidates = candidates.filter((c) => !usedCandidateIds.has(c.id));

  // Step 4: Fill any empty slots greedily with the best remaining candidates.
  // Stability comes from Step 2 (existing users keep their indices); Step 5
  // handles late-arriving higher-priority users. Gating fills on isBetterUser
  // here caused slots to stay empty when a previous occupant was displaced
  // (e.g. by the stage promotion in Step 1.5) but still present in
  // candidates, resulting in fewer than maxSlots visible tiles.
  for (let i = 0; i < maxSlots; i++) {
    if (result[i] === null && remainingCandidates.length > 0) {
      result[i] = remainingCandidates.shift()!;
      usedCandidateIds.add(result[i]!.id);
    }
  }

  // Step 5: Check if any remaining candidate should replace a visible user
  // This handles the case where someone turns on their camera and should
  // replace someone without a camera in the visible list
  for (const candidate of remainingCandidates) {
    // Find the worst user in the result that the candidate is better than
    let worstIndex = -1;
    let worstUser: T | null = null;

    for (let i = 0; i < result.length; i++) {
      const currentUser = result[i];
      if (currentUser && isBetterUser(candidate, currentUser, stagedId)) {
        // Check if this user is worse than our current worst
        if (worstUser === null || isBetterUser(worstUser, currentUser, stagedId)) {
          worstIndex = i;
          worstUser = currentUser;
        }
      }
    }

    // If we found someone to replace, do it
    if (worstIndex !== -1 && worstUser !== null) {
      result[worstIndex] = candidate;
      usedCandidateIds.add(candidate.id);
    }
  }

  const visible = result.filter((u): u is T => u !== null);

  // Step 6: Pull tiles whose hand just went up forward
  return promoteRaisedHands(previousVisible, visible, stagedId);
}

/**
 * A user that outranks a newly raised hand: the staged user, sustained
 * speakers, screen sharers — and hands raised before it, so hands keep their order.
 */
function outranksNewHand<T extends SortableUser>(
  user: T,
  earlierHands: Set<string>,
  stagedId: string | null
): boolean {
  return (
    user.id === stagedId ||
    user.isActiveSpeaker ||
    !!user.screenCastStream ||
    earlierHands.has(user.id)
  );
}

/**
 * Moves each tile whose hand went up since the previous pass (raised now, but
 * not raised or not visible before) to the first slot held by a user it
 * outranks, so the raised hand catches attention; the tiles it passes shift
 * back one slot. A raised hand never moves its tile backwards, and lowering a
 * hand moves nothing. Only the transition moves a tile, so the list stays
 * stable while the hand stays up.
 */
function promoteRaisedHands<T extends SortableUser>(
  previousVisible: T[],
  visible: T[],
  stagedId: string | null
): T[] {
  const wasRaised = new Set(previousVisible.filter((u) => u.data.hasRaisedHand).map((u) => u.id));
  const raised = visible.filter((u) => u.data.hasRaisedHand);
  const newHands = raised.filter((u) => !wasRaised.has(u.id));
  if (newHands.length === 0) return visible;
  // Only hands that are still up hold their place ahead of the new ones.
  const earlierHands = new Set(raised.filter((u) => wasRaised.has(u.id)).map((u) => u.id));

  const result = [...visible];
  for (const hand of newHands) {
    const from = result.indexOf(hand);
    const to = result.findIndex((u) => !outranksNewHand(u, earlierHands, stagedId));
    if (to !== -1 && to < from) {
      result.splice(from, 1);
      result.splice(to, 0, hand);
    }
    // Hands raised in the same pass keep their relative order.
    earlierHands.add(hand.id);
  }
  return result;
}
